import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import ProductModal from '../components/ProductModal';
import SentToProducerModal from '../components/SentToProducerModal';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Plus, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useRoleUsers } from '../hooks/useRoleUsers';
import { sortSizeEntries } from '../lib/sizeSort';

const ProductionDashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showSentToProducerModal, setShowSentToProducerModal] = useState(false);
  const [selectedProductForProducer, setSelectedProductForProducer] = useState<Product | null>(null);
  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: ''
  });

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      
      // Production tüm ürünleri görebilmeli
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSentToProducer = (productId: string) => {
    const product = products.find(p => p._id === productId);
    if (product) {
      setSelectedProductForProducer(product);
      setShowSentToProducerModal(true);
    }
  };

  const handleSentToProducerSubmit = async (data: { purchaseCode: string; referenceSku: string }) => {
    if (!selectedProductForProducer) return;

    try {
      const response = await axios.post(
        `/products/${selectedProductForProducer._id}/producer-sent`,
        {
          productionInfo: {
            purchaseCode: data.purchaseCode,
            referenceSku: data.referenceSku
          }
        }
      );
      
      if (response.data) {
        toast.success('Ürün üreticiye gönderildi ve stok girişine iletildi!');
        setShowSentToProducerModal(false);
        setSelectedProductForProducer(null);
        loadProducts();
      }
    } catch (error) {
      toast.error('İşlem başarısız oldu!');
      console.error('Sent to producer error:', error);
      throw error;
    }
  };
  
  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      const response = await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason,
        rejectedBy: user?.id
      });
      
      if (response.data) {
        toast.success('Ürün başarıyla geri yollandı');
        loadProducts();
      }
    } catch (error) {
      toast.error('Geri yollama işlemi başarısız!');
      console.error('Reject error:', error);
      throw error;
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Kategori filtreleri
    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    // Departman filtresi
    if (filters.department) {
      if (filters.department === 'nebim') {
        // Nebim kartı hem production_entry hem de nebim_entry statülerini gösterir
        filtered = filtered.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry');
      } else if (filters.department === 'publish_pending') {
        // TODO: migration sonrası legacy OR kaldırılacak
        filtered = filtered.filter(p =>
          p.status === 'publish_pending' ||
          (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
        );
      } else if (filters.department === 'image_upload') {
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.department === 'published') {
        filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
      } else {
        filtered = filtered.filter(p => p.status === filters.department);
      }
    }

    // Fiyat durumu filtresi
    if (filters.priceStatus) {
      filtered = filtered.filter(p => {
        const salePrice = p.productionInfo?.salePrice;
        const discountPrice = p.productionInfo?.discountPrice;
        
        if (filters.priceStatus === 'discounted') {
          return discountPrice && salePrice && discountPrice < salePrice;
        } else if (filters.priceStatus === 'no_discount') {
          return !discountPrice || !salePrice || discountPrice >= salePrice;
        } else if (filters.priceStatus === 'no_price') {
          return !salePrice;
        }
        return true;
      });
    }

    // Arama filtresi
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const productName = (p.productionInfo?.productName || '').toLowerCase();
        const sku = (p.nebimInfo?.sku || '').toLowerCase();
        const manufacturer = (p.productionInfo?.manufacturer || '').toLowerCase();
        const modelCode = (p.productionInfo?.modelCode || '').toLowerCase();
        
        return productName.includes(search) || 
               sku.includes(search) || 
               manufacturer.includes(search) ||
               modelCode.includes(search);
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const handleProductCreate = async (productData: any) => {
    try {
      // Ürünü oluştur (draft olarak kalacak, otomatik geçiş YOK!)
      await axios.post('/products', productData);
      
      toast.success('Ürün başarıyla oluşturuldu! Nebim\'e göndermek için "Nebim\'e Gönder" butonunu kullanın.');
      loadProducts();
      setShowModal(false);
    } catch (error) {
      toast.error('Ürün oluşturulamadı!');
      console.error('Create product error:', error);
    }
  };

  const handleProductUpdate = async (productData: any) => {
    try {
      if (!editingProduct) return;
      
      await axios.put(`/products/${editingProduct._id}`, productData);
      
      toast.success('Ürün başarıyla güncellendi!');
      loadProducts();
      setShowModal(false);
      setEditingProduct(null);
    } catch (error) {
      toast.error('Ürün güncellenemedi!');
      console.error('Update product error:', error);
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleExcelExport = async () => {
    try {
      // Excel formatında veri hazırla - Her varyant için ayrı satır
      const headers = [
        'ID',
        'Ürün Adı',
        'Üretici',
        'Ana SKU',
        'Renk Kodu',
        'Renk Adı',
        'Beden',
        'Varyant SKU',
        'Barkod',
        'Adet',
        'Durum',
        'Kategori',
        'Fiyat',
        'İndirimli Fiyat',
        'Kumaş Bilgisi',
        'Sevk Adeti',
        'Sevk Tarihi',
        'Alakasyon',
        'Oluşturma Tarihi',
        'Güncelleme Tarihi',
        'RPT Ürün'
      ];
      
      const rows: any[][] = [];
      
      filteredProducts.forEach(p => {
        const category = [
          p.categoryInfo?.mainCategory,
          p.categoryInfo?.subCategory,
          p.categoryInfo?.detailCategory
        ].filter(Boolean).join(' > ');
        
        // Alakasyon bilgilerini formatla
        const allocations = p.productionInfo?.allocationQuantities
          ? Object.entries(p.productionInfo.allocationQuantities).map(([customer, sizeAlloc]) => {
              if (typeof sizeAlloc === 'object') {
                const allocationSizes = (sizeAlloc as any).sizes || sizeAlloc;
                const total = Object.values(allocationSizes as Record<string, number>).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
                const sizes = sortSizeEntries(allocationSizes as Record<string, number>)
                  .filter(([_, qty]) => qty > 0)
                  .map(([size, qty]) => `${size}:${qty}`)
                  .join(',');
                return `${customer}(${total} adet)[${sizes}]`;
              }
              return '';
            }).join(' | ')
          : '';
        
        // Sevk tarihini formatla
        const shippingDate = p.productionInfo?.shippingDate
          ? new Date(p.productionInfo.shippingDate).toLocaleDateString('tr-TR')
          : '';
        
        // Çoklu renk varsa, her renk + beden için ayrı satır
        if (p.nebimInfo?.colorVariantBarcodes && Object.keys(p.nebimInfo.colorVariantBarcodes).length > 0) {
          Object.entries(p.nebimInfo.colorVariantBarcodes).forEach(([colorCode, sizeBarcodesMap]) => {
            // Bu renk kodu için renk adını bul
            const colorVariant = p.productionInfo?.colors?.find(c => c.code === colorCode);
            const colorName = colorVariant?.name || '';
            
            // Her beden için ayrı satır
            sortSizeEntries(sizeBarcodesMap).forEach(([size, barcode]) => {
              const sizeQuantity = p.productionInfo?.sizeDistribution?.[size] || 0;
              const shippingQuantity = p.productionInfo?.shippingQuantities?.[size] || 0;
              
              // Varyant SKU: Ana SKU + Renk Kodu + Beden
              const variantSku = `${p.nebimInfo?.sku || ''}-${colorCode}-${size}`;
              
              rows.push([
                p._id,
                p.productionInfo?.productName || '',
                p.productionInfo?.manufacturer || '',
                p.nebimInfo?.sku || '',
                colorCode,
                colorName,
                size,
                variantSku,
                barcode || '',
                sizeQuantity,
                p.status,
                category || '',
                p.productionInfo?.salePrice || '',
                p.productionInfo?.discountPrice || '',
                p.productionInfo?.fabricInfo || '',
                shippingQuantity,
                shippingDate,
                allocations,
                new Date(p.createdAt).toLocaleDateString('tr-TR'),
                new Date(p.updatedAt).toLocaleDateString('tr-TR'),
                p.isRPT === true ? 'Evet' : 'Hayır'
              ]);
            });
          });
        }
        // Tek renk (eski format) - variantBarcodes kullanılıyor
        else if (p.nebimInfo?.variantBarcodes && Object.keys(p.nebimInfo.variantBarcodes).length > 0) {
          const colorCode = p.productionInfo?.colorCode || p.productionInfo?.colors?.[0]?.code || '';
          const colorName = p.productionInfo?.colorName || p.productionInfo?.colors?.[0]?.name || '';
          
          sortSizeEntries(p.nebimInfo.variantBarcodes).forEach(([size, barcode]) => {
            const sizeQuantity = p.productionInfo?.sizeDistribution?.[size] || 0;
            const shippingQuantity = p.productionInfo?.shippingQuantities?.[size] || 0;
            
            // Varyant SKU: Ana SKU + Beden (renk kodu yoksa)
            const variantSku = colorCode 
              ? `${p.nebimInfo?.sku || ''}-${colorCode}-${size}`
              : `${p.nebimInfo?.sku || ''}-${size}`;
            
            rows.push([
              p._id,
              p.productionInfo?.productName || '',
              p.productionInfo?.manufacturer || '',
              p.nebimInfo?.sku || '',
              colorCode,
              colorName,
              size,
              variantSku,
              barcode || '',
              sizeQuantity,
              p.status,
              category || '',
              p.productionInfo?.salePrice || '',
              p.productionInfo?.discountPrice || '',
              p.productionInfo?.fabricInfo || '',
              shippingQuantity,
              shippingDate,
              allocations,
              new Date(p.createdAt).toLocaleDateString('tr-TR'),
              new Date(p.updatedAt).toLocaleDateString('tr-TR'),
              p.isRPT === true ? 'Evet' : 'Hayır'
            ]);
          });
        }
        // Barkod yoksa sadece beden dağılımı varsa
        else if (p.productionInfo?.sizeDistribution && Object.keys(p.productionInfo.sizeDistribution).length > 0) {
          const colorCode = p.productionInfo?.colorCode || p.productionInfo?.colors?.[0]?.code || '';
          const colorName = p.productionInfo?.colorName || p.productionInfo?.colors?.[0]?.name || '';
          
          sortSizeEntries(p.productionInfo.sizeDistribution).forEach(([size, quantity]) => {
            const shippingQuantity = p.productionInfo?.shippingQuantities?.[size] || 0;
            
            const variantSku = colorCode 
              ? `${p.nebimInfo?.sku || ''}-${colorCode}-${size}`
              : `${p.nebimInfo?.sku || ''}-${size}`;
            
            rows.push([
              p._id,
              p.productionInfo?.productName || '',
              p.productionInfo?.manufacturer || '',
              p.nebimInfo?.sku || '',
              colorCode,
              colorName,
              size,
              variantSku,
              '', // Barkod yok
              quantity,
              p.status,
              category || '',
              p.productionInfo?.salePrice || '',
              p.productionInfo?.discountPrice || '',
              p.productionInfo?.fabricInfo || '',
              shippingQuantity,
              shippingDate,
              allocations,
              new Date(p.createdAt).toLocaleDateString('tr-TR'),
              new Date(p.updatedAt).toLocaleDateString('tr-TR'),
              p.isRPT === true ? 'Evet' : 'Hayır'
            ]);
          });
        }
        // Hiç varyant yoksa ana ürün bilgisi
        else {
          const colorCode = p.productionInfo?.colorCode || p.productionInfo?.colors?.[0]?.code || '';
          const colorName = p.productionInfo?.colorName || p.productionInfo?.colors?.[0]?.name || '';
          
          rows.push([
            p._id,
            p.productionInfo?.productName || '',
            p.productionInfo?.manufacturer || '',
            p.nebimInfo?.sku || '',
            colorCode,
            colorName,
            '', // Beden yok
            p.nebimInfo?.sku || '', // Varyant SKU = Ana SKU
            '', // Barkod yok
            '', // Adet yok
            p.status,
            category || '',
            p.productionInfo?.salePrice || '',
            p.productionInfo?.discountPrice || '',
            p.productionInfo?.fabricInfo || '',
            '', // Sevk adeti yok
            shippingDate,
            allocations,
            new Date(p.createdAt).toLocaleDateString('tr-TR'),
            new Date(p.updatedAt).toLocaleDateString('tr-TR'),
            p.isRPT === true ? 'Evet' : 'Hayır'
          ]);
        }
      });
      
      // Excel dosyası oluştur
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      
      // Sütun genişliklerini ayarla
      const columnWidths = [
        { wch: 25 }, // ID
        { wch: 30 }, // Ürün Adı
        { wch: 20 }, // Üretici
        { wch: 15 }, // Ana SKU
        { wch: 12 }, // Renk Kodu
        { wch: 20 }, // Renk Adı
        { wch: 10 }, // Beden
        { wch: 20 }, // Varyant SKU
        { wch: 15 }, // Barkod
        { wch: 8 },  // Adet
        { wch: 15 }, // Durum
        { wch: 30 }, // Kategori
        { wch: 12 }, // Fiyat
        { wch: 12 }, // İndirimli Fiyat
        { wch: 25 }, // Kumaş Bilgisi
        { wch: 12 }, // Sevk Adeti
        { wch: 12 }, // Sevk Tarihi
        { wch: 40 }, // Alakasyon
        { wch: 15 }, // Oluşturma Tarihi
        { wch: 15 }, // Güncelleme Tarihi
        { wch: 12 }  // RPT Ürün
      ];
      worksheet['!cols'] = columnWidths;
      
      // Workbook oluştur
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ürünler');
      
      // Excel dosyasını indir
      const fileName = `urunler_varyantlar_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      toast.success(`Excel dosyası indirildi! (${rows.length} varyant satırı)`);
    } catch (error) {
      toast.error('Excel export başarısız!');
      console.error('Excel export error:', error);
    }
  };


  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content_entry: products.filter(p => p.status === 'content_entry').length,
    category_setup: products.filter(p => p.status === 'category_setup').length,
    image_upload: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    publish_pending: products.filter(p =>
      p.status === 'publish_pending' ||
      (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
    ).length,
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  // Kart tıklanınca filtreleme yap
  const handleCardClick = (status: string) => {
    if (filters.department === status) {
      // Aynı karta tekrar tıklanırsa filtreyi kaldır
      setFilters(prev => ({ ...prev, department: '' }));
    } else {
      // Yeni bir karta tıklanırsa o statüye filtrele
      setFilters(prev => ({ ...prev, department: status }));
    }
  };

  return (
    <AppLayout
      title="İlk Ürün Giriş Paneli"
      subtitle="İlk Ürün Açma & Üreticiye Gönderme"
    >
      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* Stats Cards - Hiyerarşi Adımları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* 1. Ürün Bilgileri Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 2. Nebim İşlemleri (SKU + Barkod) */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'} (SKU/Barkod)</p>
            </CardContent>
          </Card>
          
          {/* 3. Üreticiye Gönderildi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 4. Stok Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>
          
          {/* 5. Ticimaxa Aktarım */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
          
          {/* 6. İçerik Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'content_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik</CardTitle>
              <Badge variant="secondary">{stats.content_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>
          
          {/* 7. Görsel Girişi */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'image_upload' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('image_upload')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Görsel</CardTitle>
              <Badge variant="secondary">{stats.image_upload}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('visual') || 'Görsel'}</p>
            </CardContent>
          </Card>

          {/* 8. Yayın Bekliyor */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'publish_pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('publish_pending')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayın Bekliyor</CardTitle>
              <Badge variant="secondary">{stats.publish_pending}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('publish') || 'Yayın'}</p>
            </CardContent>
          </Card>

          {/* 9. Yayınlandı */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayınlanan Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-green-600 text-white">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Toplam Kart - Ayrı satırda */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.department === '' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, department: '' }))}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tüm Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.total}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Tüm ürünler (filtreyi temizle)</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktif Filtre Göstergesi */}
        {filters.department && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.department === 'draft' && 'Ürün Girişi'}
              {filters.department === 'nebim' && 'Nebim İşlemleri'}
              {filters.department === 'barcode_sent' && 'Üreticiye Gönderim'}
              {filters.department === 'stock_entry' && 'Stok Girişi'}
              {filters.department === 'api_transfer' && 'Ticimaxa Aktarım'}
              {filters.department === 'content_entry' && 'İçerik'}
              {filters.department === 'image_upload' && 'Görsel'}
              {filters.department === 'publish_pending' && 'Yayın Bekliyor'}
              {filters.department === 'published' && 'Yayınlandı'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, department: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            <Button onClick={() => {
              setEditingProduct(null);
              setShowModal(true);
            }} className="gap-2">
              <Plus className="h-4 w-4" />
              Ürün Ekle
            </Button>
            <Button variant="outline" onClick={handleExcelExport} className="gap-2">
              <FileSpreadsheet className="h-4 w-4" />
              Excel'e Aktar
            </Button>
          </div>
        </div>
        
        <Card>
          <CardContent className="p-0">
            <FilterBar 
              filters={filters}
              onFilterChange={handleFilterChange}
              products={products}
            />
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <ProductTable 
                products={filteredProducts}
                onRefresh={loadProducts}
                onSentToProducer={handleSentToProducer}
                onReject={handleReject}
                onClosureRequest={handleClosureRequest}
                onEdit={handleEdit}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ürün Ekleme/Düzenleme Modalı */}
      {showModal && (
        <ProductModal 
          onClose={() => {
            setShowModal(false);
            setEditingProduct(null);
          }}
          onSubmit={editingProduct ? handleProductUpdate : handleProductCreate}
          product={editingProduct}
        />
      )}

      {/* Üreticiye Gönderim Modalı */}
      {showSentToProducerModal && selectedProductForProducer && (
        <SentToProducerModal
          onClose={() => {
            setShowSentToProducerModal(false);
            setSelectedProductForProducer(null);
          }}
          onSubmit={handleSentToProducerSubmit}
          productName={selectedProductForProducer.productionInfo?.productName || 'Ürün'}
          initialPurchaseCode={selectedProductForProducer.productionInfo?.purchaseCode || ''}
          initialReferenceSku={selectedProductForProducer.productionInfo?.referenceSku || ''}
        />
      )}

    </AppLayout>
  );
};

export default ProductionDashboard;
